import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import styled from "@emotion/styled";
import { Box, Typography } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import CustomButton from "components/Common/CustomButton";
import AutoCompleteSmarList from "components/Common/AutoCompleteSmarList";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { debounce } from "lodash";
import { getSmarList } from "apis/componentsAction/smar";
import { ModalContext } from "contexts/ModalContext";

const Row = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "1.3rem",
  "& .label": {
    width: "25%",
    fontSize: "16px",
    color: "#4F4F4F",
    [theme.breakpoints.down("xl")]: { fontSize: "14px" },
  },
  "& .content": {
    width: "75%",
    display: "flex",
    gap: "0.5rem",
    alignItems: "flex-end",
  },
  "& .add_button_style": {
    borderRadius: "20px",
    // padding: "0.1rem 1rem 0.1rem 0.4rem",
    verticalAlign: "middle",
    "& .button_text": {
      fontSize: "16px",
      [theme.breakpoints.down("xl")]: { fontSize: "14px" },
    },
    "& .MuiButton-startIcon>*:nth-of-type(1)": {
      fontSize: "1.1rem",
      [theme.breakpoints.down("xl")]: { fontSize: "1rem" },
    },
  },
}));

const PlatformId = { facebook: 1, twitter: 2, youtube: 3, instagram: 4 };

export default function SmarGroupsField({
  action, // "create" | "edit"
  platform, // "Facebook" | "YouTube" | "Instagram"
  onChangeIds,
}) {
  const platformId = PlatformId[String(platform || "").toLowerCase()] ?? 1;
  const pageSize = 40;

  const [items, setItems] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const { showModal } = useContext(ModalContext);

  //for add smar group modal
  const modalHandler = () => {
    const modalProps = {
      type: "add page",
      action: "create",
      title: "Add SMAR Group Details",
    };
    showModal("add-smar-group-details", modalProps, "");
  };

  const fetchPage = useCallback(
    async (loadType, pageNum, search = "") => {
      const { data, error } = await getSmarList(pageNum, platformId, search);
      if (error) return;
      const results = data?.results ?? [];
      if (loadType === "initial") {
        setItems(results);
        setPageCount(Math.ceil((data?.count ?? 0) / pageSize));
        setPage(2);
      } else {
        setItems((prev) => [...prev, ...results]);
        setPage((p) => p + 1);
      }
    },
    [platformId]
  );

  const onSearch = useMemo(
    () =>
      debounce((value) => {
        const v = value || "";
        setQuery(v);
        setPage(1);
        fetchPage("initial", 1, v);
      }, 500),
    [fetchPage]
  );

  useEffect(() => {
    // reset and load when platform changes
    setItems([]);
    setPageCount(0);
    setPage(1);
    setQuery("");
    fetchPage("initial", 1, "");
  }, [platformId, fetchPage]);

  return (
    <Row>
      <span className="label">
        {action !== "edit" ? "SMAR Groups" : "Add more SMAR Groups"}
      </span>
      <div className="content">
        <FormControl sx={{ width: "80%" }} size="small">
          <AutoCompleteSmarList
            size="small"
            variant="outlined"
            limitTags={1}
            options={items}
            autoComplete="true"
            label="Select SMAR Group Name"
            onChange={(selected) => {
              if (selected?.length > 0)
                onChangeIds?.(selected.map((d) => d.pk.toString()));
              else onChangeIds?.([]);
            }}
            onInputChange={(e, value) => onSearch(value)}
            getData={() => fetchPage("next", page, query)}
            currentPage={page}
            pageCount={pageCount}
          />
        </FormControl>
        <CustomButton
          className="add_button_style"
          variant="outlined"
          startIcon={<AddCircleOutlineOutlinedIcon />}
          onClick={modalHandler}
        >
          <Typography className="button_text">Add</Typography>
        </CustomButton>
      </div>
    </Row>
  );
}
